import { ConsultationRequestStatusDTO } from '@/generated'
import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface StepItem {
  value: ConsultationRequestStatusDTO
  title: string
  subtitle: string
}

interface ExtendedStepItem extends StepItem {
  completed: boolean
  active: boolean
}

function useConsultationStepper(currentStep: Ref<ConsultationRequestStatusDTO>) {
  const { t } = useI18n()

  const stepsNotAllowedToBeActive: ConsultationRequestStatusDTO[] = [
    ConsultationRequestStatusDTO.ACCEPTED,
    ConsultationRequestStatusDTO.COMPLETED
  ]

  const allSteps: StepItem[] = [
    {
      value: ConsultationRequestStatusDTO.CREATED,
      title: t('Searching for physician'),
      subtitle: t('Waiting for answer')
    },
    {
      value: ConsultationRequestStatusDTO.ACCEPTED,
      title: t('Consulting physician assigned'),
      subtitle: t('Consultation request was accepted')
    },
    {
      value: ConsultationRequestStatusDTO.IN_PROGRESS,
      title: t('Consultation in progress'),
      subtitle: t('All participants have joined the call')
    },
    {
      value: ConsultationRequestStatusDTO.REPORT_PENDING,
      title: t('Consultation report pending'),
      subtitle: t('Waiting for the consultation report')
    },
    {
      value: ConsultationRequestStatusDTO.COMPLETED,
      title: t('Consultation completed'),
      subtitle: t('Consultation report is finalized')
    }
  ]

  function resolveStepState(step: StepItem, index: number, currentIndex: number) {
    const isBefore = index < currentIndex
    const isCurrent = index === currentIndex
    const cannotBeActive = stepsNotAllowedToBeActive.includes(step.value)

    if (isCurrent && cannotBeActive) {
      return {
        completed: true,
        active: false
      }
    }

    return {
      completed: isBefore,
      active: isCurrent
    }
  }

  const steps = computed<ExtendedStepItem[]>(() => {
    const currentIndex = allSteps.findIndex((step) => step.value === currentStep.value)

    return allSteps.map((step, index) => ({
      ...step,
      ...resolveStepState(step, index, currentIndex)
    }))
  })

  return {
    steps
  }
}

export default useConsultationStepper
